import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../hooks/AppContext';
import { api } from '../utils/api';
import { fmt } from '../utils/helpers';
import * as XLSX from 'xlsx';

export default function Reports() {
  const { activeMonth, isSimulating } = useApp();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [branchF, setBranchF] = useState('');
  const [bankF, setBankF] = useState('');
  const [issuesF, setIssuesF] = useState('');
  const [branches, setBranches] = useState([]);
  const [banks, setBanks] = useState([]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getReport({ month: activeMonth, branch: branchF, bank: bankF, issues: issuesF });
      setReportData(data);
      // Extract filter options from all data (unfiltered)
      const allData = await api.getReport({ month: activeMonth });
      setBranches([...new Set(allData.records.map(r => r.cbsbrcd))].sort());
      setBanks([...new Set(allData.records.map(r => r.bank_name).filter(Boolean))].sort());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [activeMonth, branchF, bankF, issuesF]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleDownload = () => {
    if (!reportData?.records?.length) { alert(`No records for ${activeMonth}.`); return; }
    const d = reportData.records.map(r => ({
      'Report Month': r.report_month, 'Rent For Month': r.rent_month, 'Paid In': r.paid_in,
      'CBS Branch': r.cbsbrcd, Acode: r.acode,
      'Godown ID': r.godown_id, 'Godown Name': r.godown_name,
      'Vendor ID': r.vendor_id, 'Vendor Name': r.vendor_name,
      PAN: r.pan_no, 'Account No': r.account_no, IFSC: r.ifsc, Bank: r.bank_name,
      'FA Subledger': r.fa_subledger, 'Rent Starting Period': r.rent_start_period,
      'Pay Mode': r.pay_mode, 'Rent Type': r.rent_type,
      'Monthly Rent': r.monthly_rent, SGST: r.sgst, CGST: r.cgst, IGST: r.igst,
      UTGST: r.utgst, TDS: r.tds, 'M.Ded': r.mded, 'Net Amount': r.net_amt,
      'Security Deposit': r.security_deposit, 'Other Advance': r.other_advance,
      Valid: r.status.toUpperCase(),
      'Confirmed At': r.confirmed_at ? new Date(r.confirmed_at).toLocaleString('en-IN') : '',
      Issues: r.issues.join(', '), Remarks: r.remarks,
    }));
    const ws = XLSX.utils.json_to_sheet(d);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    let fn = `Report_${activeMonth.replace(/\s/g, '')}`;
    if (branchF) fn += `_${branchF}`;
    XLSX.writeFile(wb, `${fn}.xlsx`);
  };

  return (
    <div className="wrap">
      <h2>📊 Download Reports</h2>
      <p className="sub">
        Confirmation data for: <strong style={{color: isSimulating ? 'var(--acc)' : 'var(--pri)', fontSize: 14}}>{activeMonth}</strong>
        {isSimulating ? ' (simulated)' : ' (current month)'}. Change the active month using the 🧪 simulator above.
      </p>

      <div className="card">
        <p className="text-sm text-muted" style={{marginBottom: 12}}>Additional filters — leave blank for all.</p>
        <div className="rpt-filters">
          <div className="field">
            <label>Branch</label>
            <select value={branchF} onChange={e => setBranchF(e.target.value)}>
              <option value="">All</option>
              {branches.map(b => <option key={b}>{b}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Bank</label>
            <select value={bankF} onChange={e => setBankF(e.target.value)}>
              <option value="">All</option>
              {banks.map(b => <option key={b}>{b}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Issues</label>
            <select value={issuesF} onChange={e => setIssuesF(e.target.value)}>
              <option value="">All</option>
              <option value="flagged">⚑ Flagged Only</option>
              <option value="clean">✓ No Issues</option>
            </select>
          </div>
          <div className="field" style={{display:'flex',alignItems:'flex-end',gap:6}}>
            <button className="btn btn-p" onClick={handleDownload} disabled={loading}>📥 Download</button>
            {(branchF || bankF || issuesF) && (
              <button className="clr-all" onClick={() => { setBranchF(''); setBankF(''); setIssuesF(''); }}>✕ Clear All</button>
            )}
          </div>
        </div>
        {reportData && (
          <div className="text-sm text-muted" style={{marginTop: 10}}>
            Report for <strong>{activeMonth}</strong>: <strong>{reportData.totalCount}</strong> records
            · Net: <strong>₹{fmt(reportData.totalNet)}</strong>
            · Confirmed: <strong className="text-green">₹{fmt(reportData.confirmedNet)}</strong>
          </div>
        )}
      </div>
    </div>
  );
}
